import React, { useState, useEffect } from 'react';
import { getToken } from '../utils/storage';
import SuperAdminLayout from '../components/SuperAdminLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Edit } from 'lucide-react';
import confirm from '../utils/confirm';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperAdminServiceAnnouncements = () => {
  const { t } = useTranslation();
  const [announcements, setAnnouncements] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [formData, setFormData] = useState({
    message_text: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    start_time: '00:00',
    end_time: '23:59',
    is_active: true
  });

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/admin/service-announcements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnnouncements(response.data);
    } catch (error) {
      toast.error('Kunne ikke laste servicemeldinger');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const token = getToken();
      await axios.post(`${API}/admin/service-announcements`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Servicemelding opprettet!');
      setShowModal(false);
      fetchAnnouncements();
      setFormData({
        message_text: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        start_time: '00:00',
        end_time: '23:59',
        is_active: true
      });
    } catch (error) {
      toast.error('Kunne ikke opprette servicemelding');
    }
  };

  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      message_text: announcement.message_text || '',
      start_date: announcement.start_date ? announcement.start_date.split('T')[0] : new Date().toISOString().split('T')[0],
      end_date: announcement.end_date ? announcement.end_date.split('T')[0] : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      start_time: announcement.start_time || '00:00',
      end_time: announcement.end_time || '23:59',
      is_active: announcement.is_active !== undefined ? announcement.is_active : true
    });
    setShowModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const token = getToken();
      await axios.put(`${API}/admin/service-announcements/${editingAnnouncement._id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Servicemelding oppdatert!');
      setShowModal(false);
      setEditingAnnouncement(null);
      setFormData({
        message_text: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        start_time: '00:00',
        end_time: '23:59',
        is_active: true
      });
      fetchAnnouncements();
    } catch (error) {
      toast.error('Kunne ikke oppdatere servicemelding');
    }
  };

  const handleDelete = async (announcementId) => {
    if (!await confirm.danger('Slette denne servicemeldingen?')) return;
    
    try {
      const token = getToken();
      await axios.delete(`${API}/admin/service-announcements/${announcementId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Servicemelding slettet');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Kunne ikke slette servicemelding');
    }
  };

  return (
    <SuperAdminLayout>
      <div className="admin-header">
        <h1>{t('superAdmin.serviceAnnouncements.title')}</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={20} /> {t('superAdmin.serviceAnnouncements.newAnnouncement')}
        </button>
      </div>

      <div className="admin-table">
        <div className="table-header">
          <p style={{ color: '#a0a0a0' }}>
            {t('superAdmin.serviceAnnouncements.title')}
          </p>
        </div>
        <table>
          <thead>
            <tr>
              <th>{t('superAdmin.serviceAnnouncements.message')}</th>
              <th>{t('superAdmin.serviceAnnouncements.startDate')}</th>
              <th>{t('superAdmin.serviceAnnouncements.endDate')}</th>
              <th>{t('customer.settings.startTime')}</th>
              <th>{t('common.status')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {announcements.map(ann => (
              <tr key={ann._id}>
                <td style={{ maxWidth: '400px' }}>{ann.message_text}</td>
                <td>{new Date(ann.start_date).toLocaleDateString('no-NO')}</td>
                <td>{new Date(ann.end_date).toLocaleDateString('no-NO')}</td>
                <td>
                  {ann.start_time && ann.end_time ? `${ann.start_time} - ${ann.end_time}` : '-'}
                </td>
                <td>
                  <span className={ann.is_active ? 'badge badge-success' : 'badge badge-danger'}>
                    {ann.is_active ? t('common.active') : t('common.inactive')}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      className="btn btn-secondary"
                      style={{ fontSize: '0.8rem', padding: '0.5rem 0.75rem' }}
                      onClick={() => handleEdit(ann)}
                      title={t('superAdmin.serviceAnnouncements.edit')}
                    >
                      <Edit size={14} />
                    </button>
                    <button
                      className="btn btn-danger"
                      style={{ fontSize: '0.8rem', padding: '0.5rem 0.75rem' }}
                      onClick={() => handleDelete(ann._id)}
                      title={t('superAdmin.serviceAnnouncements.delete')}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => {
          setShowModal(false);
          setEditingAnnouncement(null);
          setFormData({
            message_text: '',
            start_date: new Date().toISOString().split('T')[0],
            end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            start_time: '00:00',
            end_time: '23:59',
            is_active: true
          });
        }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingAnnouncement ? t('superAdmin.serviceAnnouncements.editAnnouncement') : t('superAdmin.serviceAnnouncements.newAnnouncement')}</h2>
            <form onSubmit={editingAnnouncement ? handleUpdate : handleCreate}>
              <div className="form-group">
                <label>{t('superAdmin.serviceAnnouncements.message')}</label>
                <textarea
                  value={formData.message_text}
                  onChange={(e) => setFormData({...formData, message_text: e.target.value})}
                  rows={3}
                  required
                  placeholder={t('superAdmin.serviceAnnouncements.message')}
                />
              </div>

              <div className="form-group">
                <label>{t('superAdmin.serviceAnnouncements.startDate')}</label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('superAdmin.serviceAnnouncements.endDate')}</label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                  required
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label>{t('customer.settings.startTime')}</label>
                  <input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label>{t('customer.settings.endTime')}</label>
                  <input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    style={{ width: 'auto' }}
                  />
                  {t('superAdmin.serviceAnnouncements.active')}
                </label>
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <button type="submit" className="btn btn-primary">
                  {editingAnnouncement ? t('superAdmin.serviceAnnouncements.update') : t('superAdmin.serviceAnnouncements.create')}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => {
                  setShowModal(false);
                  setEditingAnnouncement(null);
                  setFormData({
                    message_text: '',
                    start_date: new Date().toISOString().split('T')[0],
                    end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                    start_time: '00:00',
                    end_time: '23:59',
                    is_active: true
                  });
                }}>{t('superAdmin.serviceAnnouncements.cancel')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  );
};

export default SuperAdminServiceAnnouncements;
